import { useState, useEffect } from 'react'
import * as Sentry from '@sentry/react'
import { useTranslation } from 'react-i18next'
import { GROWSPEAK_UI_READ_ONLY_TEMP } from '../../config/growSpeakUi'
import { getKbDocuments, deleteKbDocument, ingestKbUrl } from '../../api/tenant'

const cardStyle  = { background: 'white', border: '0.5px solid #e0e3f0', borderRadius: 12, padding: 18, marginBottom: 12 }
const inputStyle = { width: '100%', padding: '8px 12px', border: '0.5px solid #e0e3f0', borderRadius: 8, fontSize: 13, background: 'white', color: '#111', outline: 'none', boxSizing: 'border-box' }
const readOnlyFieldStyle = { ...inputStyle, background: '#f5f6f8', color: '#374151', cursor: 'default' }
const directPromptDisplayStyle = { ...readOnlyFieldStyle, whiteSpace: 'pre-wrap', wordBreak: 'break-word', minHeight: 44 }
const labelStyle = { fontSize: 12, fontWeight: 600, color: '#555', marginBottom: 4, display: 'block' }

const ACCENT = '#0F6E56'

function pickDirectPrompt(record) {
  if (!record || typeof record !== 'object') return ''
  return record.direct_prompt ?? record.directPrompt ?? ''
}

function SpeakKnowledgeReadonly({ tenantId, speakbotConfig }) {
  const { t } = useTranslation()
  const [docs, setDocs]       = useState([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    let cancelled = false
    async function run() {
      setLoading(true)
      try {
        const data = await getKbDocuments(tenantId, 'speakbot')
        const list = Array.isArray(data) ? data : (data?.documents ?? [])
        if (!cancelled) setDocs(list)
      } catch (err) {
        Sentry.captureException(err, { tags: { action: 'load_kb_docs_speak_readonly' }, extra: { tenantId } })
        if (!cancelled) setDocs([])
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    run()
    return () => { cancelled = true }
  }, [tenantId])

  return (
    <div>
      <div style={cardStyle}>
        <label style={labelStyle}>{t('speakKnowledge.directPrompt')}</label>
        <div style={directPromptDisplayStyle}>{pickDirectPrompt(speakbotConfig)}</div>
      </div>

      <div style={cardStyle}>
        <p style={{ fontSize: 13, fontWeight: 600, color: '#111', marginBottom: 12 }}>{t('speakKnowledge.documents', { count: docs.length })}</p>
        {loading ? (
          <p style={{ fontSize: 13, color: '#888' }}>{t('speakKnowledge.loading')}</p>
        ) : docs.length === 0 ? (
          <p style={{ fontSize: 13, color: '#888' }}>{t('speakKnowledge.empty')}</p>
        ) : (
          <div>
            {docs.map(doc => (
              <div key={doc.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', borderBottom: '0.5px solid #f0f0f0' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ fontSize: 13, fontWeight: 500, color: '#111', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{doc.title || doc.url || doc.id}</p>
                  {doc.url && <p style={{ fontSize: 11, color: '#888', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{doc.url}</p>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

function SpeakKnowledgeEditable({ tenantId, speakbotConfig }) {
  const { t } = useTranslation()
  const [docs, setDocs]           = useState([])
  const [url, setUrl]             = useState('')
  const [loading, setLoading]     = useState(false)
  const [ingesting, setIngesting] = useState(false)

  useEffect(() => {
    let cancelled = false
    async function run() {
      setLoading(true)
      try {
        const data = await getKbDocuments(tenantId, 'speakbot')
        const list = Array.isArray(data) ? data : (data?.documents ?? [])
        if (!cancelled) setDocs(list)
      } catch (err) {
        Sentry.captureException(err, { tags: { action: 'load_kb_docs_speak' }, extra: { tenantId } })
        if (!cancelled) setDocs([])
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    run()
    return () => { cancelled = true }
  }, [tenantId])

  async function loadDocs() {
    setLoading(true)
    try {
      const data = await getKbDocuments(tenantId, 'speakbot')
      setDocs(Array.isArray(data) ? data : (data?.documents ?? []))
    } catch (err) {
      Sentry.captureException(err, { tags: { action: 'reload_kb_docs_speak' }, extra: { tenantId } })
      setDocs([])
    }
    setLoading(false)
  }

  async function handleIngest() {
    if (!url.trim()) return
    setIngesting(true)
    try {
      await ingestKbUrl(tenantId, url.trim(), 'speakbot')
      setUrl('')
      await loadDocs()
    } catch (err) {
      Sentry.captureException(err, { tags: { action: 'ingest_kb_url_speak' }, extra: { tenantId, url } })
    }
    setIngesting(false)
  }

  async function handleDelete(docId) {
    try {
      await deleteKbDocument(docId)
      setDocs(prev => prev.filter(d => d.id !== docId))
    } catch (err) {
      Sentry.captureException(err, { tags: { action: 'delete_kb_doc_speak' }, extra: { tenantId, docId } })
    }
  }

  return (
    <div>
      <div style={cardStyle}>
        <label style={labelStyle}>{t('speakKnowledge.directPrompt')}</label>
        <div style={directPromptDisplayStyle}>{pickDirectPrompt(speakbotConfig)}</div>
      </div>

      <div style={cardStyle}>
        <label style={labelStyle}>{t('speakKnowledge.addUrl')}</label>
        <div style={{ display: 'flex', gap: 8 }}>
          <input style={{ ...inputStyle, flex: 1 }} value={url} onChange={e => setUrl(e.target.value)}
            placeholder={t('speakKnowledge.urlPh')}
            onKeyDown={e => e.key === 'Enter' && handleIngest()} />
          <button type="button" onClick={handleIngest} disabled={ingesting || !url.trim()}
            style={{ background: ACCENT, color: 'white', border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: (ingesting || !url.trim()) ? 0.6 : 1, whiteSpace: 'nowrap' }}>
            {ingesting ? t('speakKnowledge.adding') : t('speakKnowledge.addUrlBtn')}
          </button>
        </div>
      </div>

      <div style={cardStyle}>
        <p style={{ fontSize: 13, fontWeight: 600, color: '#111', marginBottom: 12 }}>{t('speakKnowledge.documents', { count: docs.length })}</p>
        {loading ? (
          <p style={{ fontSize: 13, color: '#888' }}>{t('speakKnowledge.loading')}</p>
        ) : docs.length === 0 ? (
          <p style={{ fontSize: 13, color: '#888' }}>{t('speakKnowledge.empty')}</p>
        ) : (
          <div>
            {docs.map(doc => (
              <div key={doc.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', borderBottom: '0.5px solid #f0f0f0' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ fontSize: 13, fontWeight: 500, color: '#111', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{doc.title || doc.url || doc.id}</p>
                  {doc.url && <p style={{ fontSize: 11, color: '#888', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{doc.url}</p>}
                </div>
                <button type="button" onClick={() => handleDelete(doc.id)}
                  style={{ background: 'none', border: 'none', color: '#ef4444', fontSize: 12, cursor: 'pointer', marginLeft: 12, padding: '4px 8px' }}>
                  {t('speakKnowledge.delete')}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default function SpeakKnowledge({ tenantId, speakbotConfig }) {
  if (GROWSPEAK_UI_READ_ONLY_TEMP) {
    return <SpeakKnowledgeReadonly tenantId={tenantId} speakbotConfig={speakbotConfig} />
  }
  return <SpeakKnowledgeEditable tenantId={tenantId} speakbotConfig={speakbotConfig} />
}
